import { FeatureFlag } from "@coeff/api";
import { useFeatureGate } from "@statsig/react-bindings";
import React from "react";
import { useSelector } from "react-redux";
import { Redirect, Route, Switch, useRouteMatch } from "react-router-dom";
import { AppState } from "src/store";

import { TopLevelError } from "../components/TopLevelError";
import { useClientFlagsQuery } from "../hooks/useClientFlagsQuery";
import { DefaultLayout } from "../layouts";
import { usePageVisibilityLogger, useSentryLogging } from "../utils/sentry";

import { AuthPage } from "./AuthPage";
import { Billing } from "./Billing/Billing";
import { CloudTableDetails } from "./CloudTableDetails/CloudTableDetails";
import { ArchivedDashboards } from "./CoeffAdmin/ArchivedDashboards";
import { CoeffAdmin } from "./CoeffAdmin/CoeffAdmin";
import { VCRDebugger } from "./CoeffAdmin/VCRDebugger";
import { WebDashboardPlanLimit } from "./CoeffAdmin/WebDashboardPlanLimit";
import { WebDashboardTrials } from "./CoeffAdmin/WebDashboardTrials";
import { CreateCloudTable } from "./CreateCloudTable/CreateCloudTable";
import { Dashboard } from "./Dashboard/Dashboard";
import { GSheetReauthPage } from "./GSheetReauthPage";
import { LoadingRoute } from "./LoadingRoute";
import { CompleteLookerSetup } from "./LookerCompleteSetup";
import { RequestLookerSetup } from "./LookerRequestSetup";
import { NotFound } from "./NotFound";
import { ReauthPage } from "./ReauthPage";
import { SlackMarketplaceInstall } from "./SlackMarketplaceInstall";
import { StatsigSynchronousProvider } from "./StatsigSynchronousProvider";
import { WebScraperAuthSession } from "./WebScraperSession/WebScraperAuthSession";

const SuperAdminRoutes = () => {
  const { path } = useRouteMatch();
  const { value: enableDashboardRestore } = useFeatureGate(
    FeatureFlag.EnableSuperadminDashboardRestore
  );

  return (
    <DefaultLayout>
      <Switch>
        <Route exact path={path}>
          <Redirect to={`${path}/domains`} />
        </Route>
        <Route path={`${path}/domains`}>
          <CoeffAdmin />
        </Route>
        <Route path={`${path}/domainDetails`}>
          <CoeffAdmin />
        </Route>
        <Route path={`${path}/quickstart`}>
          <CoeffAdmin />
        </Route>
        <Route path={`${path}/credits`}>
          <CoeffAdmin />
        </Route>
        <Route path={`${path}/vcr_debugger`}>
          <VCRDebugger />
        </Route>
        <Route path={`${path}/recipes`}>
          <CoeffAdmin />
        </Route>
        <Route path={`${path}/smart-alerts-debug`}>
          <CoeffAdmin />
        </Route>
        <Route path={`${path}/thread-analysis`}>
          <CoeffAdmin />
        </Route>
        <Route path={`${path}/web-dashboard-trials`}>
          <WebDashboardTrials />
        </Route>
        {enableDashboardRestore && (
          <Route path={`${path}/archived-dashboards`}>
            <ArchivedDashboards />
          </Route>
        )}
        {enableDashboardRestore && (
          <Route path={`${path}/web-dashboard-plan-limit`}>
            <WebDashboardPlanLimit />
          </Route>
        )}
      </Switch>
    </DefaultLayout>
  );
};

const CoeffAdminRoutes = () => {
  const { path } = useRouteMatch();

  return (
    <DefaultLayout>
      <Switch>
        <Redirect exact from={path} to="/superadmin" />
        <Redirect from="/coeffadmin/data" to="/superadmin/domains" />
        <Redirect from="/coeffadmin/:page" to="/superadmin/:page" />
      </Switch>
    </DefaultLayout>
  );
};

const DomainAdmin_OLD = () => {
  const { path } = useRouteMatch();
  return (
    <DefaultLayout>
      <Switch>
        <Redirect exact from={path} to="/superadmin" />
        <Redirect from="/admin/:page" to="/superadmin/:page" />
      </Switch>
    </DefaultLayout>
  );
};

const RootRoutes: React.FC = () => {
  const { value: enableSentryLogging } = useFeatureGate(FeatureFlag.EnableSentryLogging);
  useSentryLogging(enableSentryLogging);
  usePageVisibilityLogger();

  return (
    <Switch>
      <Route exact path="/datasets/create" component={CreateCloudTable} />
      <Route path="/datasets/:cloudTableId" component={CloudTableDetails} />
      <Route path="/auth/:dataSourceType" component={AuthPage} />
      <Route exact path="/reauth/gsheet" component={GSheetReauthPage} />
      <Route path="/reauth/:dataSourceId" component={ReauthPage} />
      <Route path="/web_scraper/auth" component={WebScraperAuthSession} />
      <Route path="/slack-marketplace/install" component={SlackMarketplaceInstall} />
      <Route path="/superadmin" component={SuperAdminRoutes} />
      <Route path="/coeffadmin" component={CoeffAdminRoutes} />
      <Route path="/dashboard" component={Dashboard} />
      <Route path="/admin" component={DomainAdmin_OLD} />
      <Route path="/upgrade" component={Billing} />
      <Route path="/request-looker">
        <Route path="/request-looker/start" component={RequestLookerSetup} />
        <Route path="/request-looker/complete" component={CompleteLookerSetup} />
      </Route>
      <Route path="*">
        <NotFound />
      </Route>
    </Switch>
  );
};

const Root: React.FC = () => {
  const clientFlags = useSelector((state: AppState) => state.app.clientFlags);

  // Refreshes flags every 5 min / on refocus and keeps Redux in sync.
  const { isError } = useClientFlagsQuery();

  if (!clientFlags) {
    // clientFlags is briefly undefined during logout; only show the fatal
    // (Sentry-reporting) screen on an actual query failure, else stay loading.
    return isError ? <TopLevelError /> : <LoadingRoute />;
  }

  return (
    <StatsigSynchronousProvider clientFlags={clientFlags}>
      <RootRoutes />
    </StatsigSynchronousProvider>
  );
};

export default Root;
